import { apiClient } from './client';
import type {
  CelebrationTag,
  AssignTagsDto,
  TagsSnapshot,
  AITagSuggestion,
  ProcessedAISuggestion,
} from '@/types/tag.types';

export class CelebrationTagService {

  /**
   * Get all tags for a celebration (detailed with category/value info)
   */
  async getTags(celebrationId: number): Promise<CelebrationTag[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/tags`);
    return response.data.data || [];
  }

  /**
   * Get tags snapshot (denormalized format: { category: [values] })
   */
  async getTagsSnapshot(celebrationId: number): Promise<TagsSnapshot> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/tags?format=snapshot`);
    return response.data.data || {};
  }

  /**
   * Assign tags to a celebration
   */
  async assignTags(celebrationId: number, data: AssignTagsDto): Promise<CelebrationTag[]> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/tags`, data);
    return response.data.data || [];
  }

  /**
   * Assign tags from a snapshot of category/value strings
   */
  async assignTagsFromSnapshot(
    celebrationId: number,
    snapshot: TagsSnapshot,
    source: AssignTagsDto['source'] = 'ai_accepted'
  ): Promise<CelebrationTag[]> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/tags/from-snapshot`, {
      snapshot,
      source,
    });
    return response.data.data || [];
  }

  /**
   * Remove a tag from a celebration
   */
  async removeTag(celebrationId: number, tagValueId: number): Promise<void> {
    await apiClient.delete(`/celebrations/${celebrationId}/tags/${tagValueId}`);
  }

  /**
   * Confirm tags (unlocks event creation)
   */
  async confirmTags(celebrationId: number): Promise<{ confirmed: boolean; snapshot: TagsSnapshot }> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/tags/confirm`);
    return response.data.data;
  }

  /**
   * Unconfirm tags (re-locks event creation, allows re-analysis)
   */
  async unconfirmTags(celebrationId: number): Promise<{ unconfirmed: boolean }> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/tags/unconfirm`);
    return response.data.data;
  }

  /**
   * Check if tags are confirmed
   */
  async getTagsStatus(celebrationId: number): Promise<{ tags_confirmed: boolean }> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/tags/status`);
    return response.data.data;
  }

  /**
   * Analyze celebration paragraphs using AI and return tag suggestions
   */
  async analyzeParagraphs(celebrationId: number): Promise<{
    suggestions: AITagSuggestion[];
    follow_up_questions: string[];
    resolved_tags: ProcessedAISuggestion[];
  }> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/tags/analyze`);
    return response.data.data;
  }
}

export const celebrationTagService = new CelebrationTagService();
